/**
 * Work order "Payment & Invoice Settings" (v3 reference design, BE-3 data).
 *
 * Layout follows the v3 dialog: Invoice Mode chips, then Government Fee, Fine / Penalty, Add-on Charges,
 * Disbursement, Notes and a live Invoice Preview, with one Save that runs each backend call in order (the dialog is
 * covered until every call is back). Evidence — the authorisation plus the uploaded receipt — is optional for each
 * pass-through the TC pays (government fee, no-VAT add-ons, new ones too): "+ Add disbursement" opens it right where
 * it is paid. Once started, the item shows "Evidence needed" until the receipt is in, and holds back the completion. Thaklis lines have no dialog and delegated
 * lines are read-only.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Paper, Divider,
  Chip, Grid, FormControl, InputLabel, Select, IconButton, Tooltip, useTheme,
} from '@mui/material';
import {
  Undo as ReverseIcon, SwapHoriz as ReplaceIcon, Delete as DeleteIcon, Add as AddIcon, CheckCircle as PaidIcon,
} from '@mui/icons-material';
import { JournalEntryLink, DisbursementEvidenceFields, SavingOverlay } from '../../components/accounting';
import { evidenceChanged as evidenceDraftChanged, OptionalDisbursement } from '../../components/accounting/DisbursementEvidenceFields';
import { formatCurrency, formatDate } from '../../utils/accounting/formatters';
import { workOrderMoneyService } from '../../services/accounting/workOrderMoney.service';
import { serverMessage } from '../../utils/apiConvert';
import { can } from '../../utils/roleUtils';
import { cardLabel } from '../accounting/CreditCards';
import { creditCardService } from '../../services/accounting/creditCard.service';
import {
  PAYABLE_TYPES, accountTypeOf, accountTitle, needsOutcome, usePaymentLookups, buildPaymentBody, paymentSourceText,
  missingPayee, defaultPaidFrom, useServiceAccountId,
} from '../../components/accounting/PaymentSourceFields';

export const FINE_TYPES = { OVERSTAY: 'Overstay', EID_LATE: 'Emirates ID late', MOHRE_LATE: 'MOHRE late', WPS_DELAY: 'WPS delay', OTHER: 'Other' };
export const ADDON_TYPES = { TRAVEL: 'Travel', BANK_SC: 'Bank service charge', COURIER: 'Courier', OTHER: 'Other' };
export { needsOutcome };

export const isDelegatedLine = (line, tasks) =>
  (tasks || []).some((t) => (t.line_item_id || t.lineItemId) === line.id && (t.b2b_work_order_id || t.b2bWorkOrderId));

// Items a line must pay, with the live payment of each.
export const lineItemsToPay = (line, tasks, payments, addons) => {
  const delegated = isDelegatedLine(line, tasks);
  const recorded = (payments || []).filter((p) => p.status === 'RECORDED' && p.lineItemId === line.id);
  const items = [];
  const fee = Number(line.govt_charge || 0) * Number(line.quantity || 1);
  if (line.is_custom_service && !delegated && fee > 0) {
    items.push({
      key: 'GOVT', component: 'GOVT_FEE', label: 'Government fee', amount: fee, passThrough: true,
      evidence: {
        authType: line.disbursement_auth_type, receiptRef: line.disbursement_receipt_ref, status: line.disbursement_review_status,
        hasReceipt: Boolean(line.disbursement_has_receipt),
      },
      payment: recorded.find((p) => p.component === 'GOVT_FEE'),
    });
  }
  if (line.fine_amount != null) {
    items.push({
      key: 'FINE', component: 'FINE', label: `Fine — ${FINE_TYPES[line.fine_type] || line.fine_type}`, amount: Number(line.fine_amount),
      payment: recorded.find((p) => p.component === 'FINE'),
    });
  }
  (addons || []).filter((a) => a.lineItemId === line.id).forEach((a) => {
    items.push({
      key: a.id, component: 'ADDON', addon: a, label: `${ADDON_TYPES[a.addonType] || a.addonType}${a.description ? ` — ${a.description}` : ''}`,
      amount: Number(a.amount), passThrough: a.vatTreatment === 'OUT_OF_SCOPE',
      evidence: { authType: a.authType, receiptRef: a.receiptRef, status: a.reviewStatus, hasReceipt: Boolean(a.hasReceipt) },
      payment: recorded.find((p) => p.component === 'ADDON' && p.addonId === a.id),
    });
  });
  return items;
};

// The refund goes back where the money came from by default: the same account, or the same TC card.
const refundTarget = (o) => (o?.refundCardId ? `CARD:${o.refundCardId}` : o?.refundAccountId ? `ACCOUNT:${o.refundAccountId}` : '');
const defaultRefund = (p, refundAccounts) => {
  if (p.source === 'TC_CREDIT_CARD' && p.sourceCardId) return { refundCardId: p.sourceCardId, refundAccountId: undefined };
  if (p.sourceAccountId && refundAccounts.some((a) => a.id === p.sourceAccountId)) {
    return { refundAccountId: p.sourceAccountId, refundCardId: undefined };
  }
  return {};
};

// Paid-item outcome pickers, shared by cancel and remove-line.
export const PaidItemOutcomes = ({ payments, outcomes, setOutcomes, accounts }) => {
  const refundAccounts = (accounts || []).filter((a) => PAYABLE_TYPES.includes(accountTypeOf(a)));
  const [cards, setCards] = useState([]);
  useEffect(() => { creditCardService.getCards().then((c) => setCards((c || []).filter((x) => x.active !== false))).catch(() => setCards([])); }, []);
  const set = (id, patch) => setOutcomes((o) => ({ ...o, [id]: { ...o[id], ...patch } }));
  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {payments.map((p) => (
        <Paper key={p.id} variant="outlined" sx={{ p: 1.5 }}>
          <Typography fontWeight={600} fontSize="0.9rem">
            {p.component === 'GOVT_FEE' ? 'Government fee' : p.component === 'FINE' ? 'Fine' : 'Add-on'} — {formatCurrency(p.amount)}
            {p.paidToName ? ` paid to ${p.paidToName}` : ''}
          </Typography>
          <Box display="flex" gap={1} mt={1} flexWrap="wrap">
            <TextField select size="small" label="What happened? *" sx={{ minWidth: 220, flex: 1 }}
              value={outcomes[p.id]?.outcome || ''}
              onChange={(e) => set(p.id, { outcome: e.target.value, ...(e.target.value === 'REFUNDED' && !refundTarget(outcomes[p.id]) ? defaultRefund(p, refundAccounts) : {}) })}>
              <MenuItem value="MISTAKE">Recorded by mistake — never paid</MenuItem>
              <MenuItem value="REFUNDED">The payee refunded us</MenuItem>
              <MenuItem value="ABSORBED">We keep the cost</MenuItem>
            </TextField>
            {outcomes[p.id]?.outcome === 'REFUNDED' && (
              <TextField select size="small" label="Refund received into *" sx={{ minWidth: 220, flex: 1 }}
                value={refundTarget(outcomes[p.id])}
                onChange={(e) => {
                  const [kind, id] = e.target.value.split(':');
                  set(p.id, kind === 'CARD' ? { refundCardId: id, refundAccountId: undefined } : { refundAccountId: id, refundCardId: undefined });
                }}>
                {refundAccounts.map((a) => <MenuItem key={a.id} value={`ACCOUNT:${a.id}`}>{accountTitle(a)}</MenuItem>)}
                {cards.map((c) => <MenuItem key={c.id} value={`CARD:${c.id}`}>💳 {cardLabel(c)} (back to the card)</MenuItem>)}
              </TextField>
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export const outcomesComplete = (payments, outcomes) =>
  payments.every((p) => outcomes[p.id]?.outcome
    && (outcomes[p.id].outcome !== 'REFUNDED' || outcomes[p.id].refundAccountId || outcomes[p.id].refundCardId));

export const outcomesBody = (payments, outcomes) => payments.map((p) => ({
  paymentId: p.id, outcome: outcomes[p.id].outcome,
  refundAccountId: outcomes[p.id].outcome === 'REFUNDED' ? outcomes[p.id].refundAccountId : undefined,
  refundCardId: outcomes[p.id].outcome === 'REFUNDED' ? outcomes[p.id].refundCardId : undefined,
}));

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

// ── v3 "Payment & Invoice Settings" building blocks ─────────────────────────

const PAID_TO_TYPES = {
  GOVT_PORTAL: ['GOVT_PORTAL'],
  SERVICE_PROVIDER: ['AMER_CENTER', 'TASHEEL_CENTER', 'MEDICAL', 'TRANSLATION'],
  OTHER: ['OTHER'],
};
const RECORDABLE = ['IN_PROGRESS', 'ON_HOLD'];
const fs = { xs: '0.65rem', sm: '0.7rem', md: '0.75rem', lg: '0.8rem' };
const inputSx = { '& .MuiInputBase-input': { fontSize: fs.md }, '& .MuiInputLabel-root': { fontSize: fs.md } };
const groupHeaderSx = { fontSize: fs.sm, fontWeight: 600, color: 'text.secondary', opacity: '1 !important' };
const sectionSx = (borderColor) => ({ pl: 1.5, py: 1, mb: 1.5, borderLeft: '3px solid', borderColor });
const today = () => new Date().toISOString().slice(0, 10);
const money = (v) => `AED ${(Number(v) || 0).toFixed(2)}`;
const ADDON_LABEL = { TRAVEL: 'Travel', BANK_SC: 'Bank SC', COURIER: 'Courier', OTHER: 'Other' };

// A recorded payment back to the Paid From value of the selects.
const fromOfPayment = (p) => {
  if (!p) return '';
  if (p.source === 'TC_CREDIT_CARD') return `TCCARD:${p.sourceCardId}`;
  if (p.source === 'CUSTOMER_CARD') return `CUSTCARD:${p.customerCardId || ''}`;
  return p.sourceAccountId ? `ACCOUNT:${p.sourceAccountId}` : '';
};
const isCustomerCard = (from) => (from || '').startsWith('CUSTCARD:');

const PaidFromSelect = ({ value, onChange, lookups, label = 'Paid From', disabled, noCustomerCards }) => {
  const { accounts, tcCards, customerCards } = lookups;
  const active = accounts.filter((a) => a.isActive !== false);
  const banks = active.filter((a) => accountTypeOf(a) === 'OPERATIONS');
  const cash = active.filter((a) => accountTypeOf(a) === 'CASH');
  const wallets = active.filter((a) => accountTypeOf(a) === 'E_WALLET');
  const cards = tcCards.filter((c) => c.active !== false);
  const header = (text) => <MenuItem disabled sx={groupHeaderSx}>— {text} —</MenuItem>;
  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel sx={{ fontSize: fs.md }}>{label}</InputLabel>
      <Select value={value || ''} label={label} onChange={(e) => onChange(e.target.value)} sx={{ fontSize: fs.md }}>
        {banks.length > 0 && header('TC Bank Accounts')}
        {banks.map((b) => <MenuItem key={b.id} value={`ACCOUNT:${b.id}`} sx={{ fontSize: fs.md }}>🏦 {accountTitle(b)}</MenuItem>)}
        {cash.map((b) => <MenuItem key={b.id} value={`ACCOUNT:${b.id}`} sx={{ fontSize: fs.md }}>💵 {b.accountName || 'Cash Drawer'}</MenuItem>)}
        {cards.length > 0 && header('TC Credit Cards')}
        {cards.map((c) => <MenuItem key={c.id} value={`TCCARD:${c.id}`} sx={{ fontSize: fs.md }}>💳 {cardLabel(c)}</MenuItem>)}
        {!noCustomerCards && header('Customer Cards')}
        {!noCustomerCards && customerCards.length === 0 && <MenuItem value="CUSTCARD:" sx={{ fontSize: fs.md }}>💳 Customer&apos;s card (none saved)</MenuItem>}
        {!noCustomerCards && customerCards.map((c) => (
          <MenuItem key={c.id} value={`CUSTCARD:${c.id}`} sx={{ fontSize: fs.md }}>💳 {c.label || `${c.cardType} *${c.lastFour}`}</MenuItem>
        ))}        {wallets.length > 0 && header('Digital Wallets')}
        {wallets.map((b) => <MenuItem key={b.id} value={`ACCOUNT:${b.id}`} sx={{ fontSize: fs.md }}>📱 {b.accountName || b.bankName}</MenuItem>)}
      </Select>
    </FormControl>
  );
};

const BalanceHint = ({ from, lookups, amount }) => {
  if (!(from || '').startsWith('ACCOUNT:')) return null;
  const acct = lookups.accounts.find((a) => a.id === from.split(':')[1]);
  if (!acct) return null;
  const balance = Number(acct.balance || 0);
  const short = amount != null && balance - Number(amount) < 0;
  return (
    <Typography sx={{ fontSize: fs.xs, color: short ? 'error.main' : 'text.secondary', mt: 0.25, ml: 0.5 }}>
      Balance: AED {balance.toLocaleString('en-AE', { minimumFractionDigits: 2 })}{short ? ' — this payment takes it below zero' : ''}
    </Typography>
  );
};

const CustomerCardBox = ({ from, lookups, what = 'Govt fee', isDark }) => {
  if (!isCustomerCard(from)) return null;
  const card = lookups.customerCards.find((c) => c.id === from.split(':')[1]);
  return (
    <Box sx={{ p: 1, mt: 1, borderRadius: 1, bgcolor: isDark ? 'grey.900' : 'rgba(25,118,210,0.04)', border: '1px solid', borderColor: 'info.light' }}>
      {card ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={card.cardType} size="small" color="primary" sx={{ fontSize: '0.6rem', height: 18, fontWeight: 600 }} />
          <Typography sx={{ fontSize: fs.lg, fontFamily: 'monospace', fontWeight: 600 }}>**** {card.lastFour}</Typography>
          <Typography sx={{ fontSize: fs.sm, color: 'text.secondary' }}>
            {[card.cardholderName, card.expiry].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: fs.sm, color: 'text.secondary' }}>Customer&apos;s own card (no saved card on file)</Typography>
      )}
      <Typography sx={{ fontSize: '0.6rem', color: 'info.main', mt: 0.5 }}>
        {what} excluded from invoice — customer pays directly
      </Typography>
    </Box>
  );
};

const PaidToFields = ({ draft, set, providers, disabled }) => {
  const active = providers.filter((p) => p.active !== false);
  const shown = draft.paidToType ? active.filter((p) => PAID_TO_TYPES[draft.paidToType].includes(p.type)) : active;
  return (
    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
      <Grid item xs={5}>
        <FormControl fullWidth size="small" disabled={disabled}>
          <InputLabel sx={{ fontSize: fs.md }}>Type</InputLabel>
          <Select value={draft.paidToType || ''} label="Type" sx={{ fontSize: fs.md }}
            onChange={(e) => set({ paidToType: e.target.value, paidTo: '' })}>
            <MenuItem value="" sx={{ fontSize: fs.md }}><em>—</em></MenuItem>
            <MenuItem value="GOVT_PORTAL" sx={{ fontSize: fs.md }}>Govt Portal</MenuItem>
            <MenuItem value="SERVICE_PROVIDER" sx={{ fontSize: fs.md }}>Service Provider</MenuItem>
            <MenuItem value="OTHER" sx={{ fontSize: fs.md }}>Other</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={7}>
        <FormControl fullWidth size="small" disabled={disabled}>
          <InputLabel sx={{ fontSize: fs.md }}>Paid To *</InputLabel>
          <Select value={draft.paidTo || ''} label="Paid To *" sx={{ fontSize: fs.md }} onChange={(e) => set({ paidTo: e.target.value })}>
            {shown.map((p) => <MenuItem key={p.id} value={p.id} sx={{ fontSize: fs.md }}>{p.name}</MenuItem>)}
            <MenuItem value="OTHER" sx={{ fontSize: fs.md }}>Other</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {draft.paidTo === 'OTHER' && (
        <Grid item xs={12}>
          <TextField fullWidth size="small" label="Payee name *" value={draft.paidToName || ''} disabled={disabled}
            onChange={(e) => set({ paidToName: e.target.value })} sx={inputSx} />
        </Grid>
      )}
    </Grid>
  );
};

const PaidSummary = ({ payment, lookups, onReplace, onReverse, reversing, setReversing, reason, setReason, onConfirmReverse, busy }) => (
  <Box sx={{ mt: 1, p: 1, borderRadius: 1, border: '1px solid', borderColor: 'success.light', bgcolor: 'rgba(46,125,50,0.04)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <PaidIcon sx={{ fontSize: 16, color: 'success.main' }} />
      <Typography sx={{ fontSize: fs.md, flex: 1 }}>
        <b>Paid</b> {money(payment.amount)} — {payment.source === 'PARTNER' ? 'by the partner' : paymentSourceText(payment, lookups)}
        {payment.paidToName ? ` → ${payment.paidToName}` : ''} · {formatDate(payment.paidAt)}
        {payment.receiptRef ? ` · ${payment.receiptRef}` : ''}
      </Typography>
      <JournalEntryLink id={payment.journalEntryId} />
      {onReplace && <Tooltip title="Replace payment"><IconButton size="small" onClick={onReplace}><ReplaceIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
      {onReverse && <Tooltip title="Reverse payment"><IconButton size="small" color="error" onClick={() => setReversing(true)}><ReverseIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
    </Box>
    {reversing && (
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <TextField size="small" label="Reason for reversing *" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ ...inputSx, flex: 1 }} />
        <Button size="small" onClick={() => setReversing(false)} sx={{ fontSize: fs.sm, textTransform: 'none' }}>Back</Button>
        <Button size="small" variant="contained" color="error" disabled={busy || !reason.trim()} onClick={onConfirmReverse}
          sx={{ fontSize: fs.sm, textTransform: 'none' }}>Reverse</Button>
      </Box>
    )}
  </Box>
);

// A selected chip is filled in its colour; a locked one stays readable.
const ToggleChip = ({ label, selected, onClick, disabled, tone = 'primary', size = fs.xs, height = 22 }) => (
  <Chip label={label} size="small" onClick={onClick} disabled={disabled} variant={selected ? 'filled' : 'outlined'}
    sx={{
      fontSize: size, height, cursor: 'pointer', fontWeight: selected ? 600 : 400,
      ...(selected ? { bgcolor: `${tone}.main`, color: `${tone}.contrastText`, '&:hover': { bgcolor: `${tone}.dark` } } : {}),
      '&.Mui-disabled': { opacity: selected ? 0.9 : 0.45 },
    }} />
);

const emptyPay = () => ({ from: '', paidTo: '', paidToType: '', paidToName: '', reason: '' });

const WorkOrderPaymentDialog = ({ open, onClose, workOrder, line, tasks, payments, addons, onChanged, onSaved }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orgId = workOrder?.organization_id;
  const finance = can(sessionUser(), 'finance', 'write');
  const status = workOrder?.status;
  const recordable = RECORDABLE.includes(status);
  const completed = status === 'COMPLETED';
  const lookups = usePaymentLookups(open, orgId);
  const delegated = line ? isDelegatedLine(line, tasks) : false;
  const items = useMemo(() => (line ? lineItemsToPay(line, tasks, payments, addons) : []), [line, tasks, payments, addons]);

  const [mode, setMode] = useState('SIMPLE');
  const [fee, setFee] = useState(emptyPay());
  const [fine, setFine] = useState({ fineType: '', fineAmount: '', fineLiability: 'CUSTOMER_LIABILITY', ...emptyPay() });
  const [addonDrafts, setAddonDrafts] = useState({});
  const [newAddons, setNewAddons] = useState([]);
  const [evidence, setEvidence] = useState({});
  const [notes, setNotes] = useState({ paidAt: today(), receiptRef: '' });
  const [replacing, setReplacing] = useState(null);
  const [reversing, setReversing] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [notice, setNotice] = useState(null);

  const feeItem = items.find((i) => i.component === 'GOVT_FEE');
  const fineItem = items.find((i) => i.component === 'FINE');
  const addonItems = items.filter((i) => i.component === 'ADDON');
  const feeFrom = feeItem?.payment ? fromOfPayment(feeItem.payment) : fee.from;

  useEffect(() => {
    if (!open || !line) return;
    setErrors([]); setNotice(null); setReplacing(null); setReversing(null); setReason('');
    setMode(line.fine_amount != null || (addons || []).some((a) => a.lineItemId === line.id) ? 'WITH_ADDONS' : 'SIMPLE');
    setFee(emptyPay());
    setFine({ fineType: line.fine_type || '', fineAmount: line.fine_amount ?? '', fineLiability: line.fine_liability || 'CUSTOMER_LIABILITY', ...emptyPay() });
    setAddonDrafts({});
    setNewAddons([]);
    setEvidence({});
    setNotes({ paidAt: today(), receiptRef: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line?.id]);

  const serviceAccountId = useServiceAccountId(open, line?.service_id);

  // Fills only empty "Paid from" of parts not paid yet; a source the user chose is kept.
  useEffect(() => {
    if (!open || !line) return;
    const from = defaultPaidFrom(lookups.accounts, serviceAccountId);
    if (!from) return;
    const fill = (d) => (d.from ? d : { ...d, from });
    if (feeItem && !feeItem.payment) setFee(fill);
    if ((fineItem || fine.fineType) && !fineItem?.payment) setFine(fill);
    addonItems.filter((i) => !i.payment).forEach((i) => setAddonDrafts((m) => ({ ...m, [i.addon.id]: fill(m[i.addon.id] || {}) })));
    setNewAddons((n) => (n.some((x) => !x.from) ? n.map(fill) : n));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line?.id, lookups.accounts, serviceAccountId, items, fine.fineType, newAddons.length]);

  if (!line) return null;

  const wo = workOrder.id;
  const isAddonsMode = mode === 'WITH_ADDONS';

  // ── drafts (each part picks its own payment source and payee) ──
  const fineSaved = line.fine_amount != null;
  const fineDirty = Boolean(fine.fineType) && (fine.fineType !== (line.fine_type || '') || String(fine.fineAmount) !== String(line.fine_amount ?? '')
    || fine.fineLiability !== (line.fine_liability || 'CUSTOMER_LIABILITY'));
  const fineRemoved = fineSaved && !fine.fineType;
  const fineTcAbsorbs = fine.fineLiability === 'TC_LIABILITY';
  const fineDraft = (() => {
    const d = fine;
    return fineTcAbsorbs && isCustomerCard(d.from) ? { ...d, from: '' } : d;
  })();
  const setFineDraft = (patch) => setFine((f) => ({ ...f, ...patch }));
  const addonDraft = (item) => {
    const a = item.addon;
    const d = addonDrafts[a.id] || {};
    return { addonType: a.addonType, amount: String(a.amount), vatTreatment: a.vatTreatment, ...emptyPay(), ...d };
  };
  const setAddonDraft = (a) => (patch) => setAddonDrafts((m) => ({ ...m, [a.id]: { ...(m[a.id] || {}), ...patch } }));
  const addonDirty = (it, d) => d.addonType !== it.addon.addonType || Number(d.amount) !== Number(it.addon.amount) || d.vatTreatment !== it.addon.vatTreatment;
  const newDraft = (n) => n;
  const addNew = () => setNewAddons((n) => [...n, { key: `new-${Date.now()}`, addonType: 'TRAVEL', amount: '', vatTreatment: 'TAXABLE', ...emptyPay() }]);
  const setNew = (key) => (patch) => setNewAddons((n) => n.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const evidenceDraft = (item) => evidence[item.key] || { authType: item.evidence?.authType || '', receiptRef: item.evidence?.receiptRef || '' };
  const setEvidenceDraft = (item) => (patch) => setEvidence((e) => ({ ...e, [item.key]: { ...evidenceDraft(item), ...patch } }));
  const evidenceChanged = (item) => evidenceDraftChanged(evidence[item.key], item.evidence);
  // A pass-through needs evidence only when the TC's own money pays it (never the customer's card or the partner).
  const tcMoneyPaid = (p) => Boolean(p) && p.source !== 'CUSTOMER_CARD' && p.source !== 'PARTNER';
  // a draft source counts only while payments can be recorded (no evidence for a payment that cannot be made yet)
  const tcMoneyDraft = (from) => recordable && Boolean(from) && !isCustomerCard(from);
  // Sends the authorisation (when it changed) and then the picked receipt file of one pass-through.
  const saveEvidence = async (step, label, d, saved, setFn, uploadFn) => {
    const ref = (d.receiptRef || '').trim();
    if (d.authType && (d.authType !== (saved?.authType || '') || ref !== (saved?.receiptRef || ''))) {
      await step(label, () => setFn({ authType: d.authType, receiptRef: ref }));
    }
    if (d.file) await step(`${label} receipt`, () => uploadFn(d.file));
  };

  const paymentBody = (draft, component, addonId) => ({
    ...buildPaymentBody({ ...draft, paidAt: notes.paidAt, receiptRef: notes.receiptRef }, lookups), component, addonId,
  });

  // Checked before anything is saved: an amount for every fine / add-on, and a payee for every payment recorded.
  const validationErrors = () => {
    const out = [];
    if (fine.fineType && !(Number(fine.fineAmount) > 0)) out.push('Enter the fine amount');
    addonItems.forEach((it) => {
      const d = addonDraft(it);
      if (!it.payment && addonDirty(it, d) && !(Number(d.amount) > 0)) out.push(`Enter the amount for the ${ADDON_LABEL[d.addonType]} add-on`);
    });
    newAddons.forEach((n) => {
      if (!(Number(n.amount) > 0)) out.push(`Enter the amount for the ${ADDON_LABEL[n.addonType]} add-on`);
    });
    if (recordable && !delegated) {
      const paying = [];
      if (feeItem && !feeItem.payment && fee.from) paying.push(['government fee', fee]);
      if (fine.fineType && !fineItem?.payment && fineDraft.from) paying.push(['fine', fineDraft]);
      addonItems.forEach((it) => { const d = addonDraft(it); if (!it.payment && d.from) paying.push([`${ADDON_LABEL[d.addonType]} add-on`, d]); });
      newAddons.forEach((n) => { if (n.from) paying.push([`${ADDON_LABEL[n.addonType]} add-on`, n]); });
      paying.forEach(([label, d]) => { if (missingPayee(d)) out.push(`Choose who the ${label} was paid to`); });
    }
    if (replacing) {
      const it = items.find((i) => i.key === replacing);
      const d = it && (it.component === 'GOVT_FEE' ? fee : it.component === 'FINE' ? fineDraft : addonDraft(it));
      if (d && d.from && missingPayee(d)) out.push('Choose who the replacement payment was paid to');
    }
    return out;
  };

  // ── v3 single Save: every pending change, in order; the backend still gets one call per part ──
  const save = async () => {
    const invalid = validationErrors();
    if (invalid.length) { setErrors(invalid); setNotice(null); return; }
    setBusy(true); setErrors([]); setNotice(null);
    const failed = [];
    const step = async (label, fn) => {
      try { return await fn(); } catch (err) { failed.push(`${label}: ${serverMessage(err, 'failed')}`); return null; }
    };
    const payable = (item, draft) => !item.payment && draft.from && !delegated;
    const skipped = !recordable;

    if (fineRemoved) await step('Fine', () => workOrderMoneyService.clearFine(wo, line.id));
    let fineReady = fineSaved && !fineDirty && !fineRemoved;
    if (fineDirty && Number(fine.fineAmount) > 0) {
      fineReady = Boolean(await step('Fine', () => workOrderMoneyService.setFine(wo, line.id, {
        fineType: fine.fineType, fineAmount: fine.fineAmount, fineLiability: fine.fineLiability,
      })));
    }
    for (const it of addonItems) {
      const d = addonDraft(it);
      if (!it.payment && addonDirty(it, d) && Number(d.amount) > 0) {
        await step(`${ADDON_LABEL[d.addonType]} add-on`, () => workOrderMoneyService.updateAddon(wo, it.addon.id,
          { addonType: d.addonType, amount: d.amount, vatTreatment: d.vatTreatment }));
      }
    }
    const created = [];
    for (const n of newAddons) {
      if (!(Number(n.amount) > 0)) continue;
      const a = await step(`${ADDON_LABEL[n.addonType]} add-on`, () => workOrderMoneyService.addAddon(wo, line.id,
        { addonType: n.addonType, amount: n.amount, vatTreatment: n.vatTreatment }));
      if (a) created.push({ addon: a, draft: newDraft(n), key: n.key });
    }

    if (replacing) {
      const it = items.find((i) => i.key === replacing);
      const d = it.component === 'GOVT_FEE' ? fee : it.component === 'FINE' ? fineDraft : addonDraft(it);
      if (it && d.from) await step('Replace payment', () => workOrderMoneyService.replacePayment(wo, it.payment.id, d.reason.trim(), paymentBody(d, it.component, it.addon?.id)));
    }
    if (!skipped) {
      if (feeItem && replacing !== feeItem.key && payable(feeItem, fee)) {
        await step('Government fee payment', () => workOrderMoneyService.recordPayment(wo, line.id, paymentBody(fee, 'GOVT_FEE')));
      }
      if (fineReady && fineItem && replacing !== fineItem.key && payable(fineItem, fineDraft)) {
        await step('Fine payment', () => workOrderMoneyService.recordPayment(wo, line.id, paymentBody(fineDraft, 'FINE')));
      }
      if (fineDirty && !fineItem && fineReady && fineDraft.from) {
        await step('Fine payment', () => workOrderMoneyService.recordPayment(wo, line.id, paymentBody(fineDraft, 'FINE')));
      }
      for (const it of addonItems) {
        const d = addonDraft(it);
        if (replacing !== it.key && payable(it, d)) {
          await step(`${ADDON_LABEL[d.addonType]} add-on payment`, () => workOrderMoneyService.recordPayment(wo, line.id, paymentBody(d, 'ADDON', it.addon.id)));
        }
      }
      for (const c of created) {
        if (c.draft.from) {
          await step(`${ADDON_LABEL[c.addon.addonType]} add-on payment`, () => workOrderMoneyService.recordPayment(wo, line.id, paymentBody(c.draft, 'ADDON', c.addon.id)));
        }
      }
    }
    for (const it of items) {
      if (it.passThrough && evidenceChanged(it)) {
        const d = evidenceDraft(it);
        await (it.component === 'ADDON'
          ? saveEvidence(step, `${ADDON_LABEL[it.addon.addonType]} add-on evidence`, d, it.evidence,
            (body) => workOrderMoneyService.setAddonEvidence(wo, it.addon.id, body),
            (file) => workOrderMoneyService.uploadAddonReceipt(wo, it.addon.id, file))
          : saveEvidence(step, 'Disbursement evidence', d, it.evidence,
            (body) => workOrderMoneyService.setLineEvidence(wo, line.id, body),
            (file) => workOrderMoneyService.uploadLineReceipt(wo, line.id, file)));
      }
    }
    for (const c of created) {
      const d = evidence[c.key];
      if (c.addon.vatTreatment === 'OUT_OF_SCOPE' && evidenceDraftChanged(d, null)) {
        await saveEvidence(step, `${ADDON_LABEL[c.addon.addonType]} add-on evidence`, d, null,
          (body) => workOrderMoneyService.setAddonEvidence(wo, c.addon.id, body),
          (file) => workOrderMoneyService.uploadAddonReceipt(wo, c.addon.id, file));
      }
    }

    await onChanged();
    setBusy(false);
    if (failed.length) {
      setErrors(failed);
      setNewAddons([]); setAddonDrafts({}); setEvidence({}); setReplacing(null);
      return;
    }
    const pendingPayments = skipped && (feeItem ? !feeItem.payment && fee.from : false);
    if (pendingPayments) {
      setNotice(`Saved. Payments can be recorded once the work order is In Progress or On Hold (now ${String(status || '').replace('_', ' ').toLowerCase()}).`);
      setNewAddons([]); setAddonDrafts({}); setEvidence({});
      return;
    }
    if (onSaved) onSaved('Payment details saved');
    onClose();
  };

  const reverse = async (item) => {
    setBusy(true); setErrors([]);
    try {
      await workOrderMoneyService.reversePayment(wo, item.payment.id, reason.trim());
      setNotice('Payment reversed');
      setReversing(null); setReason('');
      await onChanged();
    } catch (err) {
      setErrors([serverMessage(err, 'The reversal failed')]);
    } finally {
      setBusy(false);
    }
  };

  // Paid summary, or the Paid From / Paid To fields (also when replacing).
  const paymentFields = (item, draft, setDraft, what, { noCustomerCards = false } = {}) => {
    const p = item?.payment;
    if (p && replacing !== item.key) {
      return (
        <>
          <PaidSummary payment={p} lookups={lookups} busy={busy}
            onReplace={finance && !delegated && p.source !== 'PARTNER' ? () => { setReplacing(item.key); setDraft({ ...emptyPay() }); } : null}
            onReverse={finance && !delegated && !completed && p.source !== 'PARTNER' ? true : null}
            reversing={reversing === item.key} setReversing={(v) => { setReason(''); setReversing(v ? item.key : null); }}
            reason={reason} setReason={setReason} onConfirmReverse={() => reverse(item)} />
          {p.source === 'CUSTOMER_CARD' && (
            <Typography sx={{ fontSize: '0.6rem', color: 'info.main', mt: 0.5 }}>{what} excluded from invoice — customer pays directly</Typography>
          )}
        </>
      );
    }
    if (delegated) return <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', mt: 1 }}>Paid by the partner typing centre.</Typography>;
    return (
      <Box sx={{ mt: 1 }}>
        {replacing && replacing === item?.key && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField size="small" label="Reason for replacing *" value={draft.reason || ''} onChange={(e) => setDraft({ reason: e.target.value })} sx={{ ...inputSx, flex: 1 }} />
            <Button size="small" onClick={() => setReplacing(null)} sx={{ fontSize: fs.sm, textTransform: 'none' }}>Keep payment</Button>
          </Box>
        )}
        <PaidFromSelect value={draft.from} lookups={lookups} onChange={(v) => setDraft({ from: v })} noCustomerCards={noCustomerCards} />
        <BalanceHint from={draft.from} lookups={lookups} amount={item?.amount} />
        <CustomerCardBox from={draft.from} lookups={lookups} what={what} isDark={isDark} />
        <PaidToFields draft={draft} set={setDraft} providers={lookups.providers} />
      </Box>
    );
  };

  // Clears an item's saved evidence (the evidence is optional again).
  const removeEvidence = async (removeFn) => {
    setBusy(true); setErrors([]);
    try {
      await removeFn();
      setNotice('Disbursement removed');
      await onChanged();
    } catch (err) {
      setErrors([serverMessage(err, 'Removing the disbursement failed')]);
    } finally {
      setBusy(false);
    }
  };

  // optional: hidden behind "+ Add disbursement" until added; only started evidence holds back the completion
  const evidenceFields = (item, fetchUrl, removeFn) => (
    <OptionalDisbursement dense saved={item.evidence} disabled={completed || busy}
      onDiscard={() => setEvidence((e) => { const next = { ...e }; delete next[item.key]; return next; })}
      onRemove={() => removeEvidence(removeFn)}>
      <DisbursementEvidenceFields dense draft={evidenceDraft(item)} set={setEvidenceDraft(item)} status={item.evidence?.status}
        hasReceipt={item.evidence?.hasReceipt} fetchUrl={fetchUrl} disabled={completed || busy}
        onError={(m) => setErrors([m])} />
    </OptionalDisbursement>
  );

  const addonFields = (d, set, locked, onDelete) => (
    <>
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={6}>
          <FormControl size="small" fullWidth disabled={locked}>
            <InputLabel sx={{ fontSize: fs.md }}>Type</InputLabel>
            <Select value={d.addonType} label="Type" sx={{ fontSize: fs.md }} onChange={(e) => set({ addonType: e.target.value })}>
              <MenuItem value="TRAVEL" sx={{ fontSize: fs.md }}>Travel</MenuItem>
              <MenuItem value="BANK_SC" sx={{ fontSize: fs.md }}>Bank SC</MenuItem>
              <MenuItem value="COURIER" sx={{ fontSize: fs.md }}>Courier</MenuItem>
              <MenuItem value="OTHER" sx={{ fontSize: fs.md }}>Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={5}>
          <TextField size="small" fullWidth label="Amount" type="number" value={d.amount} disabled={locked}
            onChange={(e) => set({ amount: e.target.value })} inputProps={{ min: 0, step: '0.01' }}
            InputProps={{ endAdornment: <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', whiteSpace: 'nowrap' }}>AED</Typography> }} sx={inputSx} />
        </Grid>
        <Grid item xs={1}>
          {onDelete && (
            <IconButton size="small" onClick={onDelete} disabled={busy} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Grid>
      </Grid>
      <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', mr: 0.5 }}>VAT:</Typography>
        <ToggleChip label="Taxable (5%)" selected={d.vatTreatment === 'TAXABLE'} disabled={locked} onClick={() => set({ vatTreatment: 'TAXABLE' })} />
        <ToggleChip label="No VAT" tone="secondary" selected={d.vatTreatment === 'OUT_OF_SCOPE'} disabled={locked} onClick={() => set({ vatTreatment: 'OUT_OF_SCOPE' })} />
        <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', ml: 0.5 }}>
          {d.vatTreatment === 'TAXABLE' ? 'TC revenue' : 'Pass-through (disbursement)'}
        </Typography>
      </Box>
    </>
  );

  // ── invoice preview (v3; follows the backend's billing rules) ──
  const qty = Number(line.quantity || 1);
  const govtFee = feeItem ? feeItem.amount : 0;
  const feeCustomerPaid = isCustomerCard(feeFrom);
  const sc = Number(line.service_charge || 0) * qty;
  const scVat = Number(line.tax_value || 0) * qty;
  const fineAmt = Number(fine.fineAmount || 0);
  const fineFrom = fineItem?.payment ? fromOfPayment(fineItem.payment) : fineDraft.from;
  const fineCustomerCard = isCustomerCard(fineFrom);
  const fineOnInvoice = fine.fineType && fine.fineLiability === 'CUSTOMER_LIABILITY' && !fineCustomerCard ? fineAmt : 0;
  const fineAsExpense = fine.fineType && fine.fineLiability === 'TC_LIABILITY' ? fineAmt : 0;
  const previewAddons = [
    ...addonItems.map((it) => {
      const d = addonDraft(it);
      const from = it.payment ? fromOfPayment(it.payment) : d.from;
      return { key: it.key, type: d.addonType, amount: Number(d.amount || 0), taxable: d.vatTreatment === 'TAXABLE', from, vat: addonDirty(it, d) ? null : it.addon.vatAmount };
    }),
    ...newAddons.map((n) => ({ key: n.key, type: n.addonType, amount: Number(n.amount || 0), taxable: n.vatTreatment === 'TAXABLE', from: newDraft(n).from })),
  ];
  let addonOnInvoice = 0;
  let addonVat = 0;
  previewAddons.forEach((a) => {
    if (isCustomerCard(a.from)) return;
    addonOnInvoice += a.amount;
    if (a.taxable) addonVat += a.vat != null ? Number(a.vat) : a.amount * 0.05;
  });
  const govtFeeOnInvoice = feeCustomerPaid ? 0 : govtFee;
  const totalVat = scVat + addonVat;
  const total = govtFeeOnInvoice + sc + scVat + fineOnInvoice + addonOnInvoice + addonVat;
  const sourceName = (from) => {
    if (!from) return null;
    const [kind, id] = from.split(':');
    if (kind === 'ACCOUNT') { const a = lookups.accounts.find((x) => x.id === id); return a ? accountTitle(a) : null; }
    if (kind === 'TCCARD') { const c = lookups.tcCards.find((x) => x.id === id); return c ? cardLabel(c) : null; }
    return 'Customer card';
  };
  const usedSources = new Set([sourceName(feeFrom), fine.fineType ? sourceName(fineFrom) : null, ...previewAddons.map((a) => sourceName(a.from))].filter(Boolean));
  const PreviewRow = ({ label, amount, color = 'text.secondary', bold, sub }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2, pl: sub ? 1.5 : 0 }}>
      <Typography sx={{ fontSize: sub ? fs.sm : fs.md, color, fontWeight: bold ? 600 : 400 }}>{label}</Typography>
      <Typography sx={{ fontSize: sub ? fs.sm : fs.md, color, fontWeight: bold ? 600 : 400 }}>{money(amount)}</Typography>
    </Box>
  );
  const ExcludedRow = ({ label, value = 'excluded', color = 'info.main' }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
      <Typography sx={{ fontSize: fs.sm, color }}>{label}</Typography>
      <Typography sx={{ fontSize: fs.sm, color }}>{value}</Typography>
    </Box>
  );
  const fineLabel = FINE_TYPES[fine.fineType] || fine.fineType;
  const somethingUnpaid = !delegated && (items.some((i) => !i.payment) || newAddons.length > 0 || (fineDirty && !fineItem));
  const canSave = !busy && (!feeItem || feeItem.payment || fee.from || delegated);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} disableEscapeKeyDown={busy} maxWidth="sm" fullWidth
      PaperProps={{ sx: { position: 'relative' } }}>
      <SavingOverlay open={busy} />
      <DialogTitle sx={{ fontSize: '0.9rem', fontWeight: 600, pb: 1 }}>
        Payment & Invoice Settings
        <Typography component="span" sx={{ fontSize: fs.md, color: 'text.secondary', display: 'block' }}>
          {line.service_name || 'Service'}{line.customer_name ? ` - ${line.customer_name}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 1.5, fontSize: fs.sm }} onClose={() => setErrors([])}>
              {errors.length === 1 ? errors[0] : (
                <>Some parts were not saved:<ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{errors.map((e) => <li key={e}>{e}</li>)}</ul></>
              )}
            </Alert>
          )}
          {notice && <Alert severity="success" sx={{ mb: 1.5, fontSize: fs.sm }} onClose={() => setNotice(null)}>{notice}</Alert>}
          {delegated && (
            <Alert severity="info" sx={{ mb: 1.5, fontSize: fs.sm }}>
              Delegated to a partner typing centre — the partner pays its items and bills you; they are settled in Partner Payments.
            </Alert>
          )}

          {/* Invoice Mode */}
          <Typography sx={{ fontSize: fs.lg, fontWeight: 600, mb: 0.5 }}>Invoice Mode</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <ToggleChip label="Simple (Fee + SC)" selected={mode === 'SIMPLE'} onClick={() => setMode('SIMPLE')} size={fs.md} height={26} />
            <ToggleChip label="With Add-ons" selected={isAddonsMode} onClick={() => setMode('WITH_ADDONS')} size={fs.md} height={26} />
          </Box>

          {/* ── 1. Government Fee ── */}
          <Box sx={sectionSx('primary.main')}>
            <Typography sx={{ fontSize: fs.md, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              Government Fee{feeItem ? ` · ${money(feeItem.amount)}` : ''}
            </Typography>
            {feeItem ? paymentFields(feeItem, fee, (patch) => setFee((f) => ({ ...f, ...patch })), 'Govt fee') : (
              <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', fontStyle: 'italic' }}>
                {delegated ? 'Paid by the partner typing centre.' : 'This service has no government fee to pay.'}
              </Typography>
            )}
          </Box>

          {/* ── 2. Fine / Penalty ── */}
          {isAddonsMode && (
            <Box sx={sectionSx('warning.main')}>
              <Typography sx={{ fontSize: fs.md, fontWeight: 600, color: 'text.secondary', mb: 1 }}>Fine / Penalty</Typography>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={fine.fineType ? 6 : 12}>
                  <FormControl fullWidth size="small" disabled={delegated || Boolean(fineItem?.payment)}>
                    <InputLabel sx={{ fontSize: fs.md }}>Fine Type</InputLabel>
                    <Select value={fine.fineType || ''} label="Fine Type" sx={{ fontSize: fs.md }} onChange={(e) => setFineDraft({ fineType: e.target.value })}>
                      <MenuItem value="" sx={{ fontSize: fs.md }}><em>None</em></MenuItem>
                      {Object.entries(FINE_TYPES).map(([v, l]) => <MenuItem key={v} value={v} sx={{ fontSize: fs.md }}>{l}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                {fine.fineType && (
                  <Grid item xs={5}>
                    <TextField fullWidth size="small" label="Amount" type="number" value={fine.fineAmount} disabled={delegated || Boolean(fineItem?.payment)}
                      onChange={(e) => setFineDraft({ fineAmount: e.target.value })} inputProps={{ min: 0, step: '0.01' }}
                      InputProps={{ endAdornment: <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', whiteSpace: 'nowrap' }}>AED</Typography> }} sx={inputSx} />
                  </Grid>
                )}
                {fine.fineType && (
                  <Grid item xs={1}>
                    {!delegated && !fineItem?.payment && (
                      <IconButton size="small" onClick={() => setFineDraft({ fineType: '', fineAmount: '' })}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Grid>
                )}
              </Grid>
              {fine.fineType && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', mr: 0.5 }}>Liability:</Typography>
                  <ToggleChip label="Customer" selected={fine.fineLiability === 'CUSTOMER_LIABILITY'}
                    disabled={delegated || Boolean(fineItem?.payment)} onClick={() => setFineDraft({ fineLiability: 'CUSTOMER_LIABILITY' })} />
                  <ToggleChip label="TC absorbs" tone="warning" selected={fine.fineLiability === 'TC_LIABILITY'}
                    disabled={delegated || Boolean(fineItem?.payment)}
                    onClick={() => setFineDraft({ fineLiability: 'TC_LIABILITY', ...(isCustomerCard(fine.from) ? { from: '' } : {}) })} />
                  <Typography sx={{ fontSize: '0.6rem', color: fine.fineLiability === 'TC_LIABILITY' ? 'warning.main' : 'text.secondary', ml: 0.5 }}>
                    {fine.fineLiability === 'TC_LIABILITY' ? 'Expense — not on invoice' : 'Pass-through — on invoice, no VAT'}
                  </Typography>
                </Box>
              )}
              {delegated && fineSaved && line.fine_liability === 'CUSTOMER_LIABILITY' && (
                <Button size="small" variant="outlined" sx={{ mt: 1, fontSize: fs.sm, textTransform: 'none' }} disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try { await workOrderMoneyService.absorbFine(wo, line.id); setNotice('The fine is now your cost and will not be billed to the customer'); await onChanged(); }
                    catch (err) { setErrors([serverMessage(err, 'Absorbing the fine failed')]); }
                    finally { setBusy(false); }
                  }}>
                  Absorb the partner&apos;s fine (don&apos;t bill the customer)
                </Button>
              )}
              {fine.fineType && paymentFields(fineItem || { key: 'FINE' }, fineDraft, setFineDraft, 'Fine', { noCustomerCards: fineTcAbsorbs })}
            </Box>
          )}

          {/* ── 3. Add-on Charges ── */}
          {isAddonsMode && (
            <Box sx={sectionSx('info.main')}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Typography sx={{ fontSize: fs.md, fontWeight: 600, color: 'text.secondary' }}>Add-on Charges</Typography>
                {!delegated && (
                  <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={addNew}
                    sx={{ fontSize: fs.sm, textTransform: 'none', minWidth: 0, px: 1 }}>Add</Button>
                )}
              </Box>
              {addonItems.length === 0 && newAddons.length === 0 && (
                <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', fontStyle: 'italic' }}>No add-on charges yet.</Typography>
              )}
              {addonItems.map((it) => {
                const a = it.addon;
                const d = addonDraft(it);
                const set = setAddonDraft(a);
                const locked = delegated || Boolean(it.payment);
                const tcMoney = it.payment ? tcMoneyPaid(it.payment) : tcMoneyDraft(d.from);
                return (
                  <Box key={it.key} sx={{ mb: 1, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    {addonFields(d, set, locked, locked ? null : async () => {
                      setBusy(true);
                      try { await workOrderMoneyService.removeAddon(wo, a.id); await onChanged(); }
                      catch (err) { setErrors([serverMessage(err, 'Removing the add-on failed')]); }
                      finally { setBusy(false); }
                    })}
                    {paymentFields(it, d, set, 'Add-on')}
                    {!delegated && d.vatTreatment === 'OUT_OF_SCOPE' && tcMoney && (
                      <Box sx={{ mt: 1 }}>
                        {evidenceFields(it, it.evidence?.hasReceipt ? () => workOrderMoneyService.addonReceiptUrl(wo, a.id) : null,
                          () => workOrderMoneyService.removeAddonEvidence(wo, a.id))}
                      </Box>
                    )}
                  </Box>
                );
              })}
              {newAddons.map((n) => {
                const d = newDraft(n);
                return (
                  <Box key={n.key} sx={{ mb: 1, p: 1.5, borderRadius: 1, border: '1px dashed', borderColor: 'info.light' }}>
                    {addonFields(n, setNew(n.key), false, () => setNewAddons((list) => list.filter((x) => x.key !== n.key)))}
                    {paymentFields(null, d, setNew(n.key), 'Add-on')}
                    {n.vatTreatment === 'OUT_OF_SCOPE' && tcMoneyDraft(d.from) && (
                      <Box sx={{ mt: 1 }}>{evidenceFields({ key: n.key, evidence: null }, null)}</Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* ── 4. Disbursement (VATP013) — evidence for a government fee the TC pays ── */}
          {feeItem && !delegated && (feeItem.payment && replacing !== feeItem.key ? tcMoneyPaid(feeItem.payment) : tcMoneyDraft(fee.from)) && (
            <Box sx={sectionSx('text.secondary')}>
              <Typography sx={{ fontSize: fs.md, fontWeight: 600, color: 'text.secondary', mb: 1 }}>Disbursement</Typography>
              {evidenceFields(feeItem, feeItem.evidence?.hasReceipt ? () => workOrderMoneyService.lineReceiptUrl(wo, line.id) : null,
                () => workOrderMoneyService.removeLineEvidence(wo, line.id))}
            </Box>
          )}

          {/* ── 5. Notes — paid date and receipt, used for every payment this Save records ── */}
          {somethingUnpaid && (
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 1.5 }}>
              <TextField size="small" type="date" label="Paid on" InputLabelProps={{ shrink: true }} inputProps={{ max: today() }}
                value={notes.paidAt} onChange={(e) => setNotes((n) => ({ ...n, paidAt: e.target.value }))} sx={{ ...inputSx, width: 160 }} />
              <TextField size="small" label="Notes" placeholder="Receipt number, transaction reference, comments..."
                value={notes.receiptRef} onChange={(e) => setNotes((n) => ({ ...n, receiptRef: e.target.value }))} sx={{ ...inputSx, flex: 1 }} />
            </Box>
          )}
          {somethingUnpaid && !recordable && (
            <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mt: -1, mb: 1.5 }}>
              Payments (and their evidence) are recorded once the work order is In Progress or On Hold; fines and add-ons save now.
            </Typography>
          )}

          {/* ── 6. Invoice Preview ── */}
          <Box sx={{ p: 1.5, bgcolor: isDark ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
            <Typography sx={{ fontSize: fs.lg, fontWeight: 600, mb: 0.5 }}>Invoice Preview</Typography>
            {govtFeeOnInvoice > 0 && <PreviewRow label="Govt Fee (no VAT)" amount={govtFeeOnInvoice} />}
            {feeItem && feeCustomerPaid && <ExcludedRow label="Govt Fee — customer paid directly" />}
            <PreviewRow label="Service Charge" amount={sc} />
            <PreviewRow label="VAT on service charge" amount={scVat} sub />
            {fineOnInvoice > 0 && <PreviewRow label={`Fine (${fineLabel}) — customer liability`} amount={fineOnInvoice} />}
            {fineAsExpense > 0 && <ExcludedRow label="Fine — TC expense (not on invoice)" value={money(fineAsExpense)} color="warning.main" />}
            {fineAmt > 0 && fineCustomerCard && fine.fineLiability === 'CUSTOMER_LIABILITY' && <ExcludedRow label="Fine — customer paid directly" />}
            {previewAddons.filter((a) => a.amount > 0).map((a) => (isCustomerCard(a.from)
              ? <ExcludedRow key={a.key} label={`${ADDON_LABEL[a.type] || a.type} — customer paid`} />
              : (
                <Box key={a.key}>
                  <PreviewRow label={`${ADDON_LABEL[a.type] || a.type}${a.taxable ? '' : ' (no VAT)'}`} amount={a.amount} />
                  {a.taxable && <PreviewRow label="VAT" amount={a.vat != null ? Number(a.vat) : a.amount * 0.05} sub />}
                </Box>
              )))}
            <Divider sx={{ my: 0.5 }} />
            <PreviewRow label="Invoice Total" amount={total} bold color="text.primary" />
            {totalVat > 0 && <PreviewRow label="Total VAT included" amount={totalVat} sub />}
            {usedSources.size > 1 && (
              <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', mt: 0.5 }}>Paid from: {Array.from(usedSources).join(' · ')}</Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ fontSize: fs.lg, textTransform: 'none' }}>Cancel</Button>
        {!delegated && (
          <Button variant="contained" size="small" onClick={save} disabled={!canSave} sx={{ fontSize: fs.lg, textTransform: 'none' }}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WorkOrderPaymentDialog;
